import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "checkbox",
    "count",
    "filter",
    "toggleAll",
    "search",
    "searchClear",
    "searchResult",
    "searchEmpty",
    "searchTerm",
    "widen",
  ]
  static values = {submitSelector: String}

  connect() {
    this.currentFilter = "present"
    this.searchTerm = ""
    this.applyFilter()
  }

  filter(event) {
    this.currentFilter = event.currentTarget.dataset.filter
    this.applyFilter()
  }

  showAll() {
    this.currentFilter = "all"
    this.applyFilter()
  }

  applyFilter() {
    this.checkboxTargets.forEach((checkbox) => {
      if (checkbox.dataset.locked !== "true") {
        this.setChecked(checkbox, false)
      }
    })

    this.updateVisibility()
    this.highlightActiveFilter()
    this.refreshSelectionState()
  }

  search() {
    this.searchTerm = this.hasSearchTarget ? this.searchTarget.value : ""
    this.updateVisibility()
    this.refreshSelectionState()
  }

  clearSearch() {
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.searchTarget.focus()
    }

    this.search()
  }

  // The search field sits inside the photo form: Enter would submit the tags.
  preventSubmit(event) {
    event.preventDefault()
  }

  updateVisibility() {
    this.checkboxTargets.forEach((checkbox) => {
      const visible = this.inFilterScope(checkbox) && this.matchesSearch(checkbox)

      this.itemFor(checkbox)?.classList.toggle("hidden", !visible)
    })

    this.refreshSearchFeedback()
  }

  toggleAll() {
    const checked = !this.allVisibleSelected

    this.visibleCheckboxes.forEach((checkbox) => this.setChecked(checkbox, checked))
    this.refreshSelectionState()
  }

  highlightActiveFilter() {
    this.filterTargets.forEach((button) => {
      const active = button.dataset.filter === this.currentFilter

      button.classList.toggle("btn-primary", active)
      button.classList.toggle("btn-default", !active)
      button.setAttribute("aria-pressed", active ? "true" : "false")
    })
  }

  refreshSelectionState() {
    const selectedCount = this.selectedCheckboxes.length

    if (this.hasCountTarget) {
      this.countTarget.textContent = selectedCount
    }

    if (this.submitElement) {
      const isDisabled = selectedCount === 0

      this.submitElement.disabled = isDisabled
      this.submitLabel = isDisabled ? this.disabledSubmitLabel : this.enabledSubmitLabel
    }

    if (this.hasToggleAllTarget) {
      this.refreshToggleAll()
    }
  }

  refreshToggleAll() {
    const button = this.toggleAllTarget
    const nothingToToggle = this.visibleCheckboxes.length === 0

    button.textContent = this.allVisibleSelected ? this.deselectAllLabel : this.selectAllLabel
    button.classList.toggle("disabled", nothingToToggle)
    button.setAttribute("aria-disabled", nothingToToggle ? "true" : "false")
  }

  refreshSearchFeedback() {
    const searching = this.searchTerm.trim() !== ""
    const hits = this.visibleCheckboxes.length

    if (this.hasSearchClearTarget) {
      this.searchClearTarget.classList.toggle("hidden", !searching)
    }

    if (this.hasSearchResultTarget) {
      this.searchResultTarget.classList.toggle("hidden", !searching || hits === 0)
      this.searchResultTarget.textContent = `${hits} van ${this.filterScopeCheckboxes.length}`
    }

    if (this.hasSearchEmptyTarget) {
      this.searchEmptyTarget.classList.toggle("hidden", !searching || hits > 0)
    }

    if (this.hasSearchTermTarget) {
      this.searchTermTarget.textContent = this.searchTerm.trim()
    }

    if (this.hasWidenTarget) {
      this.widenTarget.classList.toggle("hidden", this.currentFilter === "all")
    }
  }

  // "all" means members of the group; a child present via a combined group is not one.
  inFilterScope(checkbox) {
    if (checkbox.dataset.locked === "true") {
      return true
    }

    if (this.currentFilter === "all") {
      return checkbox.dataset.inGroup !== "false"
    }

    return checkbox.dataset.present === "true"
  }

  matchesSearch(checkbox) {
    const terms = this.normalize(this.searchTerm).split(/\s+/).filter(Boolean)
    if (terms.length === 0) {
      return true
    }

    const words = this.normalize(checkbox.dataset.name || "").split(/[\s-]+/)

    return terms.every((term) => words.some((word) => word.startsWith(term)))
  }

  normalize(text) {
    return text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
  }

  itemFor(checkbox) {
    return checkbox.closest("li")
  }

  setChecked(checkbox, checked) {
    if (checkbox.disabled || checkbox.checked === checked) {
      return
    }

    checkbox.checked = checked
    checkbox.dispatchEvent(new Event("change", {bubbles: true}))
  }

  get enabledCheckboxes() {
    return this.checkboxTargets.filter((checkbox) => !checkbox.disabled)
  }

  get selectedCheckboxes() {
    return this.enabledCheckboxes.filter((checkbox) => checkbox.checked)
  }

  get filterScopeCheckboxes() {
    return this.enabledCheckboxes.filter((checkbox) => this.inFilterScope(checkbox))
  }

  get visibleCheckboxes() {
    return this.enabledCheckboxes.filter((checkbox) => !this.itemFor(checkbox)?.classList.contains("hidden"))
  }

  get allVisibleSelected() {
    const visible = this.visibleCheckboxes

    return visible.length > 0 && visible.every((checkbox) => checkbox.checked)
  }

  get selectAllLabel() {
    return this.toggleAllTarget.dataset.selectLabel || "Alles selecteren"
  }

  get deselectAllLabel() {
    return this.toggleAllTarget.dataset.deselectLabel || "Alles deselecteren"
  }

  get submitElement() {
    return this.hasSubmitSelectorValue ? document.querySelector(this.submitSelectorValue) : null
  }

  get enabledSubmitLabel() {
    return this.submitElement?.dataset.enabledLabel || "Opslaan"
  }

  get disabledSubmitLabel() {
    return this.submitElement?.dataset.disabledLabel || "Opslaan (selecteer minstens 1 kind)"
  }

  set submitLabel(label) {
    if (!this.submitElement) {
      return
    }

    if (this.submitElement.tagName === "INPUT") {
      this.submitElement.value = label
    } else {
      this.submitElement.textContent = label
    }
  }
}
